/* eslint-disable import/no-webpack-loader-syntax */
import React, { useContext, useState, useCallback, useRef, useEffect } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import Map, { Source, Layer } from 'react-map-gl';

import { GlobalContext } from '../../contexts/global.context';
import { TabInfoContext } from '../../contexts/tab-info.context';

import { checkIfArraysContainSameValues, regionBBoxes, stateFips, stateFipsToUsps, fetchFromAcis } from '../../utilities';

import LocationPins from './location-pins/location-pins.component';
import LocationSearch from './location-search/location-search.component';
import Button from '../button/button.component';
import NationalForestsToggle from './national-forests-toggle/national-forests-toggle.component';

import './map.styles.scss';

// eslint-disable-next-line @typescript-eslint/no-var-requires
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;
// Set token for mapbox API
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;


// Bounding box used to set initial view
const INIT_BOUNDS = [-126, 34, -66, 43];

// Bounding box used to limit panning
const BBOX = {
  north: 53.3,
  south: 19.8,
  east: -54.0,
  west: -137.8,
};

// Clamps latitude
function isOutsideLatBounds(lat) {
  return lat > BBOX.north || lat < BBOX.south;
}

// Clamps longitude
function isOutsideLngBounds(lng) {
  return lng > BBOX.east || lng < BBOX.west;
}

function getSearchLocation(terms, bbox, token) {
  const bboxString = [bbox.west, bbox.south, bbox.east, bbox.north].join(',');
  return fetch(
    `https://api.mapbox.com/search/geocode/v6/forward?access_token=${token}&country=US&bbox=${bboxString}&q=${terms.trim()}`
  )
    .then(response => response.json())
    .then(results => {
      let location = results.features[0];
    
      const address = location.properties.full_address.replace(', United States','');
      const lat = location.properties.coordinates.latitude;
      const lng = location.properties.coordinates.longitude;

      return {
        id: String(Date.now()),
        address,
        lat,
        lng,
      };
    })
    .catch(() => false);
}

// Gets the address name for a set of coordinates if it is within the list of allowed states
function getClickedLocation(lat, lng, token) {
  if (isOutsideLatBounds(lat) || isOutsideLngBounds(lng)) {
    return false;
  }

  return fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?limit=1&access_token=${token}`,
    { method: 'GET' }
  )
    .then((response) => response.json())
    .then((res) => {
      const feature = res.features[0];
      const countryCode =
        feature.context[feature.context.length - 1].short_code;
      if (countryCode !== 'us') {
        throw new Error('Out of bounds');
      }

      const addressParts = ['place', 'district', 'region'].reduce((address, addressPartName) => {
        const context = feature.context.find(({ id }) => id.includes(addressPartName));
        if (context) {
          address.push(context.text);
        }
        return address;
      }, []);

      return {
        id: String(Date.now()),
        address: addressParts.length > 0 ? addressParts.join(', ') : 'Unnamed Location',
        lat,
        lng,
      };
    })
    .catch(() => false);
}

const consolidateColorDefs = (colorData, regions) => {
  const hiddenColor = 'rgba(0,0,0,0)';
  const defaultColor = 'rgba(0,0,0,0.05)';
  
  let colors = [
    'match',
    ['get', 'GEOID']
  ];
  
  if (Object.keys(colorData).length === 0 || regions.length === 0) {
    return hiddenColor;
  }
  Object.keys(colorData).forEach(regName => {
    if (colorData[regName] !== undefined) {
      if (regions.includes(regName)) {
        colors = colors.concat(colorData[regName]);
      } else {
        colors = colors.concat(colorData[regName].map((v, i) => i % 2 === 0 ? v : hiddenColor));
      }
    }
  });

  colors.push(defaultColor);
  return colors;
};

function HoverInfoDisplay({ hoverInfo }) {
  if (hoverInfo === null) {
    return '';
  } else {
    return (
      <div className='map-hover-info' style={{ zIndex: 4, pointerEvents: 'none' }}>
        {hoverInfo.countyName ? <div className='map-hover-info-county-name'>{hoverInfo.countyName}</div> : ''}
        {hoverInfo.forestName ?
          <div className='map-hover-info-forest-name'>{hoverInfo.forestName}</div>
          :
          <div className='map-hover-info-spacer'></div>
        }
        {hoverInfo.countyValue ? <div className='map-hover-info-county-value'>{hoverInfo.countyValue}</div> : ''}
        {'countyBiomass' in hoverInfo ? <div className='map-hover-info-county-biomass'>{hoverInfo.countyBiomass} tons/acre</div> : ''
        }
      </div>
    );
  }
}

export default function MapComponent() {
  const mapRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [viewState, setViewState] = useState({
    bounds: INIT_BOUNDS,
  });
  const [initView, setInitView] = useState(null);
  const [isInitView, setIsInitView] = useState(true);
  const [prevRegions, setPrevRegions] = useState([]);

  const [forestOverlayVisibility, setForestOverlayVisibility] = useState(false);

  const {
    selectedSubTab,
    display,
    toggleDisplay,
    overlayData,
    overlayColors,
    getSelectorValueByIdFromTabInformation,
    pastLocations,
    handleUpdate,
    biomassInfo
    } = useContext(GlobalContext);
  const { componentFns } = useContext(TabInfoContext);

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.loadImage(`${process.env.PUBLIC_URL}/assets/stripes.png`, (error, image) => {
          if (error) throw error;
          if (!map.hasImage('crosshatch')) map.addImage('crosshatch', image);
      });
    }
  }, [mapRef.current]);

  useEffect(() => {
    try {
      const currCenter = mapRef.current.getCenter();
      const currZoom = mapRef.current.getZoom();

      setIsInitView(
        currCenter.lat === initView.center[1] &&
          currCenter.lng === initView.center[0] &&
          currZoom === initView.zoom
      );
    } catch {
      setIsInitView(true);
    }
  }, [viewState, initView]);

  // Tries to get new location information, stores it if successful, moves map to new location
  const handleLocationChange = async (e, eventType) => {
    let results = null;
    if (eventType === 'click') {
      results = await getClickedLocation(e.lngLat.lat, e.lngLat.lng, process.env.REACT_APP_MAPBOX_TOKEN);
    } else if (eventType === 'search') {
      results = await getSearchLocation(e.terms, BBOX, process.env.REACT_APP_MAPBOX_TOKEN);
    }
    
    if (results) {
      const center = [results.lng, results.lat];
      
      try {
        // Dont care about the results, just that this can execute. Will throw an error if there is no ACIS data at the location
        await fetchFromAcis({
          loc: center.join(','),
          grid: 'nrcc-nn',
          date: '2020-01-01',
          elems: [{ name: 'pcpn' }]
        });

        const newPastLocs = {
          ...pastLocations,
          [results.id]: results,
        };
  
        handleUpdate('addLocation', { newId: results.id, newPastLocations: newPastLocs });
        if (display === 'map') toggleDisplay();
  
        if (mapRef.current) {
          mapRef.current.flyTo({
            center,
            speed: 0.8,
            essential: true,
          });
        }
      } catch (e) {
        console.error(e)
      }
    }
  };

  // Tries to get new location information, stores it if successful, moves map to new location
  const handleZoomToBbox = async (regions) => {
    if (mapRef.current) {
      if (regions.length === 0 || regions.length === Object.keys(regionBBoxes).length) {
        resetViewState();
      } else {
        const bbox = regions.reduce((acc, regName) => {
          const bbox = regionBBoxes[regName];
          if (bbox[0][0] < acc[0][0]) acc[0][0] = bbox[0][0];
          if (bbox[0][1] < acc[0][1]) acc[0][1] = bbox[0][1];
          if (bbox[1][0] > acc[1][0]) acc[1][0] = bbox[1][0];
          if (bbox[1][1] > acc[1][1]) acc[1][1] = bbox[1][1];
          return acc;
        }, [[180,90],[-180,-90]]);
        mapRef.current.fitBounds(bbox, { padding: 30 });
      }
    }
  };

  // Limits panning to bbox coordinates
  const handlePanning = (view) => {
    if (isOutsideLatBounds(view.latitude)) {
      view.latitude = viewState.latitude;
    }

    if (isOutsideLngBounds(view.longitude)) {
      view.longitude = viewState.longitude;
    }

    setViewState((prev) => {
      return {
        ...prev,
        ...view,
      };
    });
  };

  // Increases zoom step and sets init zoom for reset button to use
  const handleLoad = (e) => {
    e.target.scrollZoom.setWheelZoomRate(1 / 100);

    const center = e.target.getCenter();
    setInitView({
      zoom: e.target.getZoom(),
      center: [center.lng, center.lat],
    });
  };

  // Moves map to initial view
  const resetViewState = () => {
    mapRef.current.flyTo({
      ...initView,
      speed: 0.8,
      essential: true,
    });
  };

  const onHover = useCallback((event, data, display) => {
    const hasForestFeature = Boolean(display === 'map' && event.features && event.features.find(f => f.sourceLayer === 'national_forests'));
    const hasCountyFeature = Boolean(display === 'map' && event.features && event.features.find(f => f.sourceLayer === 'cb_2019_us_county_500k-9krtz6'));
    
    let newHoverInfo = (hasForestFeature || hasCountyFeature) ? { countyName: null, countyValue: null, countyBiomass: null, forestName: null } : null;

    if (hasForestFeature) {
      const forestFeature = event.features.find(f => f.sourceLayer === 'national_forests');
      newHoverInfo.forestName = forestFeature.properties['COMMONNAME'];
    }
    
    if (hasCountyFeature) {
      const countyFeature = event.features.find(f => f.sourceLayer === 'cb_2019_us_county_500k-9krtz6');
      const countyProperties = countyFeature.properties;
      const countyFips = countyProperties.GEOID;
      const colorDef = Object.values(countyFeature.layer.paint['fill-color']);
      
      for (const regionObj of Object.values(data)) {
        if (countyFips in regionObj) {
          if (JSON.stringify(colorDef) === '[0,0,0,0]') {
            newHoverInfo.countyValue = 'County is in hidden region';
          } else if (JSON.stringify(colorDef) === '[0,0,0,0.05]') {
            newHoverInfo.countyValue = 'Affected tree species not present in this county';
          } else {
            const mapTooltipFormatter = (componentFns && 'mapTooltipFormatter' in componentFns) ? componentFns['mapTooltipFormatter'] : null;
            newHoverInfo.countyValue = mapTooltipFormatter ? mapTooltipFormatter(regionObj[countyFips], { selectedSubTab, getSelectorValueByIdFromTabInformation }) : regionObj[countyFips] + '%';
          }
          break;
        }
      }

      if (newHoverInfo.countyValue === null && newHoverInfo.forestName === null) {
        newHoverInfo = null;
      } else {
        let name = '';
        if (countyProperties.STATEFP === '11') {
          name = 'District of Columbia';
        } else {
          let countySuffix = 'County';
          if (countyProperties.STATEFP === '22') {
            countySuffix = 'Parish';
          } else if ((countyProperties.STATEFP === '51' && parseInt(countyProperties.GEOID) > 51500) || ['24510', '29510', '32510'].includes(countyProperties.GEOID)) {
            countySuffix = 'City';
          }

          const stateUsps = stateFipsToUsps(countyProperties.STATEFP);
          name = `${countyProperties.NAME} ${countySuffix}, ${stateUsps}`;
        }

        newHoverInfo.countyName = name;

        // Get biomass for county for specific indicator
        const reformattedName = selectedSubTab.toLowerCase().split(' ').join('_');
        const biomassByCounty = Object.entries(biomassInfo).find(([name,obj]) => reformattedName.includes(name))[1];
        const biomass = biomassByCounty[countyFips];
        newHoverInfo.countyBiomass = biomass === undefined ? '0' : (biomass === 0 ? 'Trace' : biomass);
      }
    }
    setHoverInfo(newHoverInfo);
  }, [selectedSubTab, componentFns]);

  const overlayKey = getSelectorValueByIdFromTabInformation('overlayKey');
  const selectedOverlayData = overlayData ? overlayData[overlayKey] : [];
  const selectedOverlayColors = overlayColors ? overlayColors[overlayKey] : [];
  const selectedRegions = getSelectorValueByIdFromTabInformation('regions');
  
  // Anytime the selected regions change, zoom to the selected ones
  if (selectedRegions && prevRegions && !checkIfArraysContainSameValues(selectedRegions, prevRegions)) {
    setPrevRegions(selectedRegions);
    handleZoomToBbox(selectedRegions);
  }

  return (
    <div className='map-container'>
      {display === 'map' && !isInitView &&
        <Button buttonType='resetZoom' onClick={resetViewState} style={{ zIndex: 4, top: 'unset', bottom: '32px', left: '8px' }} >
          Reset Zoom
        </Button>
      }

      {display === 'map' ? (
        <>
          <LocationSearch handleSearch={(terms) => handleLocationChange({ terms }, 'search')} />
          <NationalForestsToggle visibility={forestOverlayVisibility} setVisibility={setForestOverlayVisibility} />
          <HoverInfoDisplay hoverInfo={hoverInfo} />
        </>
      ) : ''}

      <Map
        {...viewState}
        ref={mapRef}
        mapStyle='mapbox://styles/precipadmin/cmd7fqbno00i001sa353jha2p'
        boxZoom={false}
        dragRotate={false}
        touchPitch={false}
        doubleClickZoom={false}
        attributionControl={false}
        onMove={(evt) => handlePanning(evt.viewState)}
        onMouseMove={(e) => onHover(e, selectedOverlayData, display)}
        onClick={(e) => handleLocationChange(e, 'click')}
        onLoad={handleLoad}
        minZoom={3.1}
        interactiveLayerIds={['county-fill', 'forest-fill']}
        projection='mercator'
      >
        <LocationPins />

        <Source
          type='vector'
          url='mapbox://precipadmin.b2ztd99d'
          id='county-colors'
        >
          <Layer
            id='county-fill'
            type='fill'
            source-layer='cb_2019_us_county_500k-9krtz6'
            paint={{
              'fill-color': consolidateColorDefs(selectedOverlayColors, selectedRegions),
              'fill-opacity': 0.5,
            }}
            filter={['in', ['get', 'STATEFP'], ['literal', stateFips]]}
          />
        </Source>

        <Source
          type='vector'
          url='mapbox://precipadmin.9vz092eg'
          id='national-forests'
        >
          <Layer
            id='forest-fill'
            type='fill'
            source-layer='national_forests'
            layout={{ visibility: forestOverlayVisibility ? 'visible': 'none' }}
            paint={{
              'fill-pattern': 'crosshatch',
              'fill-opacity': 0.5
            }}
          />
          <Layer
            id='forest-line'
            type='line'
            source-layer='national_forests'
            layout={{ visibility: forestOverlayVisibility ? 'visible': 'none' }}
            paint={{
              'line-color': '#0C4E0C',
              'line-opacity': 0.5,
              'line-width': 2
            }}
          />
        </Source>
      </Map>
    </div>
  );
}
